import Icon from '@components/Icon/Icon';
import { CloseIcon } from '@components/Icons/CloseIcon';
import { DownloadIcon } from '@components/Icons/DownloadIcon';
import { FavoriteIcon } from '@components/Icons/FavoriteIcon';
import { ForwardIcon } from '@components/Icons/ForwardIcon';
import { Likedicon } from '@components/Icons/LikedIcon';
import { PauseIcon } from '@components/Icons/PauseIcon';
import { PlayIcon } from '@components/Icons/PlayIcon';
import { PlayNextIcon } from '@components/Icons/PlayNextIcon';
import { PrevIcon } from '@components/Icons/PrevIcon';
import { UpArrowIcon } from '@components/Icons/UpArrowIcon';
import { UnloggedSignupModal } from '@components/Modals/UnloggedSignupModal';
import { NapsterSpinner } from '@components/NapsterSpinner/NapsterSpinner';
import { Tooltip } from '@components/Tooltip';
import { useMusicPlayer } from '@hooks/musicPlayer';
import { useStationMode } from '@hooks/station';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import { useAppRouting } from '@hooks/useAppRouting';
import { useFavorites } from '@hooks/useFavorites';
import { useIsDesktop } from '@hooks/useIsDesktop';
import { trackCreationDownloaded } from '@utils/analyticsHelpers';
import { extraQueryParams } from '@utils/constants';
import { getContentType } from '@utils/contentType';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Config, placeHolderData } from '../../.../../config';
import {
  getPlaylistTracks,
  getPlaylists,
  removeTrackFromPlaylist,
} from '../../services/api';
import { setPlaylistsByCategory } from '../../store/reducers/commonStore';
import { useAppDispatch } from '../../store/store';
import { showToast } from '../../utils/toast';
import { AddToPlaylistModal } from './components/AddToPlaylistModal';
import { LanguageSelector } from './components/LanguageSelector';
import MusicInfo from './components/MusicInfo';
import PlayerControls from './components/PlayerControls';
import { ConfirmRemoveModal } from './components/PlaylistQueue/ConfirmRemoveModal';
import ShareTooltip from './components/ShareTooltip';
import { StationSharePanel } from './components/StationSharePanel';
import { VolumeControl } from './components/VolumeControl';

export const Musicplayer = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    activePlaylist,
    setActivePlaylist,
    shuffleEnabled,
    repeatMode,
    setShuffleEnabled,
    setRepeatMode,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setVolume: _setVolume,
  } = useMusicState();

  // Get current track for playlist contributor extraction
  const currentTrackForContributor =
    activePlaylist?.album?.tracks?.[activePlaylist?.currentIndex];

  // For playlists, use track contributors; for albums, use album contributors
  const contributor =
    activePlaylist?.album?.type === 'playlist'
      ? currentTrackForContributor?.contributors?.[0]
      : activePlaylist?.album?.contributors?.[0];

  const {
    activePopup,
    setActivePopup,
    isMinimized,
    setIsMinimized,
    playlistOpen,
  } = useCommonState();
  const isDesktopRaw = useIsDesktop(768);
  const isStationMode = useStationMode();
  const isDesktop = isStationMode ? false : isDesktopRaw;
  const navigate = useNavigate();
  const {
    isAuthenticated,
    signinRedirect,
    isLoading: isAuthLoading,
  } = useAuth();

  // Use the music player hook
  const {
    audioRef,
    videoRef,
    isPlaying,
    currentTime,
    duration,
    progress,
    isLoading,
    isBuffering,
    userHasInteracted,
    volume,
    handlePlayPause,
    handleNext,
    handlePrev,
    handleSeek,
    handleTrackSelect,
    handleEnded,
    handleVolumeChange,
    formatTime,
    setIsPlaying,
  } = useMusicPlayer({ isMinimized });

  // Component-specific state (not related to music player)
  const [isClosing, setIsClosing] = useState(false);
  // const [hasReducedVolumeForSession, setHasReducedVolumeForSession] =
  //   useState(false);
  const [hasPausedForSession, setHasPausedForSession] = useState(false);
  const [hasAnimatedIn, setHasAnimatedIn] = useState(false);
  const [isMinimizing, setIsMinimizing] = useState(false);
  const [isMaximizing, setIsMaximizing] = useState(false);
  const [isAddToPlaylistModalOpen, setIsAddToPlaylistModalOpen] =
    useState(false);
  const [isShareTooltipOpen, setIsShareTooltipOpen] = useState(false);
  const [isStationSharePanelOpen, setIsStationSharePanelOpen] = useState(false);
  const [hasAutoOpenedSharePanel, setHasAutoOpenedSharePanel] = useState(false);
  const [hasAutoMinimizedForPricing, setHasAutoMinimizedForPricing] =
    useState(false);
  const [isUnloggedPlaybackModalOpen, setIsUnloggedPlaybackModalOpen] =
    useState(false);
  const [isPlaybackBlocked, setIsPlaybackBlocked] = useState(false);
  const [hasReachedFiftyPercent, setHasReachedFiftyPercent] = useState(false);
  const [isMiniPlayerDragging, setIsMiniPlayerDragging] = useState(false);
  const [miniPlayerDragProgress, setMiniPlayerDragProgress] = useState(0);
  const [isMiniPlayerSeeking, setIsMiniPlayerSeeking] = useState(false);
  const miniPlayerBarRef = React.useRef<HTMLDivElement | null>(null);
  const miniPlayerSeekTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const miniPlayerTargetProgressRef = React.useRef<number | null>(null);

  const wait = (ms: number) => new Promise((res) => setTimeout(res, ms));

  const isGeneratedTrackSaved = () => {
    if (!activePlaylist || activePlaylist.album.category !== 'generated') {
      return true;
    }
    const trackId =
      activePlaylist.album.tracks[activePlaylist.currentIndex]?.id;
    if (!trackId) return false;
    const TEMP_ID_PREFIX = 'temp-generated-';
    return !trackId.startsWith(TEMP_ID_PREFIX);
  };

  // Generate share URL for current track (same logic as PlayerControls)
  const generateShareUrl = () => {
    if (!activePlaylist) return window.location.origin;

    const baseUrl = window.location.origin;
    const albumId = activePlaylist.album.id;
    const trackId =
      activePlaylist.album.tracks[activePlaylist.currentIndex]?.id;

    // Use different URL format based on album category
    if (activePlaylist.album.type === 'playlist') {
      return trackId
        ? `${baseUrl}/playlist/${albumId}/${trackId}`
        : window.location.origin;
    } else if (
      activePlaylist.album.category === 'my-creations' ||
      activePlaylist.album.category === 'generated'
    ) {
      const orgId = activePlaylist.album.orgId;
      return trackId && orgId
        ? `${baseUrl}/creations/track/${orgId}/${trackId}`
        : window.location.origin;
    }

    // Clean, simple share URL - meta tags will be injected by server/client
    return trackId
      ? `${baseUrl}/player/${albumId}/${trackId}`
      : window.location.origin;
  };

  // Handler for opening Add to Playlist modal with authentication check
  const handleAddToPlaylistClick = () => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      console.log('User not authenticated, redirecting to login...');
      const fullPath = window.location.pathname + window.location.search;
      signinRedirect({
        extraQueryParams,
        state: { returnUrl: fullPath },
      }).catch((error) => {
        console.error('Error during sign-in redirect:', error);
      });
      return;
    }
    // Open the modal if authenticated
    setIsAddToPlaylistModalOpen(true);
  };

  // Handler for downloading creation track
  const handleDownloadCreation = () => {
    if (!activePlaylist || activePlaylist.album.category !== 'my-creations') {
      return;
    }
    const currentTrack =
      activePlaylist.album.tracks[activePlaylist.currentIndex];
    if (!currentTrack) return;

    try {
      const link = document.createElement('a');
      link.href = currentTrack.url || '';
      link.download = `${currentTrack.name || 'track'}.mp3`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast.success(`Downloading ${currentTrack.name}`);

      // Analytics: creation_downloaded
      const contributor = activePlaylist?.album?.contributors?.[0];
      const contentType = getContentType(activePlaylist?.album?.category);
      trackCreationDownloaded({
        creationId: currentTrack.id,
        creationType: contentType,
        creationName: currentTrack.name,
        creationCompanion: contributor?.givenName || 'Unknown',
      });
    } catch (error) {
      console.error('Error downloading track:', error);
      showToast.error(t('player.downloadFailed'));
    }
  };

  // Wrapper for handlePlayPause to prevent playback when blocked
  const handlePlayPauseWithBlockCheck = React.useCallback(async () => {
    // skip block check if enable sign up modal is disabled
    if (!Config.ENABLE_50_PERCENT_SIGNUP_MODAL) {
      handlePlayPause();
      return;
    }

    // If playback is blocked AND current position is at or past 50%, prevent playback
    if (isPlaybackBlocked && progress >= 50) {
      console.log('🎵 Playback blocked - current position at or past 50%');
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setIsPlaying(false);
      return;
    }
    // If playback is blocked but position is before 50%, allow playback
    handlePlayPause();
  }, [isPlaybackBlocked, handlePlayPause, progress]);

  // Wrapper for handleSeek to prevent seeking past 50% when blocked
  const handleSeekWithBlockCheck = React.useCallback(
    (percentage: number) => {
      // skip block check if enable sign up modal is disabled
      if (!Config.ENABLE_50_PERCENT_SIGNUP_MODAL) {
        handleSeek(percentage);
        return;
      }

      if (isPlaybackBlocked && percentage > 50) {
        console.log('🎵 Seek blocked - cannot seek past 50% without login');
        // Seek back to 50% if user tries to go further
        if (audioRef.current && duration > 0) {
          const maxTime = (duration * 50) / 100;
          audioRef.current.currentTime = maxTime;
        }
        return;
      }
      handleSeek(percentage);
    },
    [isPlaybackBlocked, handleSeek, duration],
  );

  const calcMiniPlayerPercentage = React.useCallback((clientX: number) => {
    if (!miniPlayerBarRef.current) return 0;
    const rect = miniPlayerBarRef.current.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    return (x / rect.width) * 100;
  }, []);

  const handleMiniPlayerDragStart = React.useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (duration <= 0 || isNaN(duration)) {
        console.warn('Mini player dragging disabled: Invalid duration', {
          duration,
        });
        return;
      }

      e.preventDefault();
      if (miniPlayerSeekTimeoutRef.current) {
        clearTimeout(miniPlayerSeekTimeoutRef.current);
        miniPlayerSeekTimeoutRef.current = null;
      }
      setIsMiniPlayerSeeking(false);
      setIsMiniPlayerDragging(true);
      const percentage =
        'clientX' in e
          ? calcMiniPlayerPercentage(e.clientX)
          : calcMiniPlayerPercentage(e.touches[0].clientX);
      setMiniPlayerDragProgress(percentage);
      handleSeekWithBlockCheck(percentage);
    },
    [duration, handleSeekWithBlockCheck, calcMiniPlayerPercentage],
  );

  const handleMiniPlayerDragMove = React.useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isMiniPlayerDragging) return;
      e.preventDefault();
      const percentage =
        'clientX' in e
          ? calcMiniPlayerPercentage(e.clientX)
          : calcMiniPlayerPercentage(e.touches[0].clientX);
      setMiniPlayerDragProgress(percentage);
      handleSeekWithBlockCheck(percentage);
    },
    [isMiniPlayerDragging, handleSeekWithBlockCheck, calcMiniPlayerPercentage],
  );

  const handleMiniPlayerDragEnd = React.useCallback(() => {
    if (miniPlayerSeekTimeoutRef.current) {
      clearTimeout(miniPlayerSeekTimeoutRef.current);
    }
    miniPlayerTargetProgressRef.current = miniPlayerDragProgress;
    setIsMiniPlayerSeeking(true);
    handleSeekWithBlockCheck(miniPlayerDragProgress);
    setIsMiniPlayerDragging(false);
    miniPlayerSeekTimeoutRef.current = setTimeout(() => {
      setIsMiniPlayerSeeking(false);
      miniPlayerTargetProgressRef.current = null;
    }, 300);
  }, [miniPlayerDragProgress, handleSeekWithBlockCheck]);

  React.useEffect(() => {
    if (isMiniPlayerDragging) {
      window.addEventListener('mousemove', handleMiniPlayerDragMove);
      window.addEventListener('mouseup', handleMiniPlayerDragEnd);
      window.addEventListener('touchmove', handleMiniPlayerDragMove);
      window.addEventListener('touchend', handleMiniPlayerDragEnd);
    } else {
      window.removeEventListener('mousemove', handleMiniPlayerDragMove);
      window.removeEventListener('mouseup', handleMiniPlayerDragEnd);
      window.removeEventListener('touchmove', handleMiniPlayerDragMove);
      window.removeEventListener('touchend', handleMiniPlayerDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleMiniPlayerDragMove);
      window.removeEventListener('mouseup', handleMiniPlayerDragEnd);
      window.removeEventListener('touchmove', handleMiniPlayerDragMove);
      window.removeEventListener('touchend', handleMiniPlayerDragEnd);
      if (miniPlayerSeekTimeoutRef.current) {
        clearTimeout(miniPlayerSeekTimeoutRef.current);
      }
    };
  }, [isMiniPlayerDragging, handleMiniPlayerDragMove, handleMiniPlayerDragEnd]);

  React.useEffect(() => {
    if (
      isMiniPlayerSeeking &&
      miniPlayerTargetProgressRef.current !== null &&
      Math.abs(progress - miniPlayerTargetProgressRef.current) < 1
    ) {
      if (miniPlayerSeekTimeoutRef.current) {
        clearTimeout(miniPlayerSeekTimeoutRef.current);
      }
      setIsMiniPlayerSeeking(false);
      miniPlayerTargetProgressRef.current = null;
    }
  }, [progress, isMiniPlayerSeeking]);

  // Callback to notify parent components when track is added to playlist
  const handleTrackAddedToPlaylist = async (playlistId?: string) => {
    if (!playlistId || !activePlaylist) return;

    const category = activePlaylist.album.category as
      | 'music'
      | 'serenity'
      | 'book';

    try {
      // Refetch all playlists for this category from API
      console.log(`📡 Fetching ${category} playlists from API...`);
      const response = await getPlaylists(category, 50, 0);

      if (response?.items) {
        console.log(
          `✅ Fetched ${category} playlists (${response.items.length} items)`,
        );
        // Update Redux store with all playlists for this category
        dispatch(
          setPlaylistsByCategory({
            category,
            playlists: response.items,
          }),
        );
        console.log(`📦 Redux store updated with ${category} playlists`);
      }
    } catch (error) {
      console.error('Error refetching playlists after add:', error);
    }
  };

  // Confirmation modal state for removing from playlist
  const [confirmRemoveTrackId, setConfirmRemoveTrackId] = useState<
    string | null
  >(null);
  const [confirmRemoveTrackName, setConfirmRemoveTrackName] = useState('');
  const [isRemoving, setIsRemoving] = useState(false);

  const location = useLocation();
  const params = useParams();

  // Track IDs that should show video mode even for my-creations
  const videoModeTracks = ['1d88879c-a8f6-484a-bd7a-5b2392356609'];

  // Get current track ID and track
  const currentTrackId =
    activePlaylist?.album.tracks[activePlaylist.currentIndex]?.id;
  const currentTrackForCreation =
    activePlaylist?.album.tracks[activePlaylist.currentIndex];

  // Show album art instead of video for my-creations, creation playlist tracks, or tracks with itemType='creation' (unless in videoModeTracks)
  const showAlbumArtForCreations =
    (activePlaylist?.album.category === 'my-creations' ||
      activePlaylist?.album.category === 'creation' ||
      (currentTrackForCreation as any)?.itemType === 'creation') &&
    !videoModeTracks.includes(currentTrackId || '');

  // Check authentication for protected player routes
  // /player/ and /creations/track/ are public, but /playlist/ requires login
  useEffect(() => {
    const currentPath = location.pathname;
    const isPlaylistRoute = currentPath.startsWith('/playlist/');

    // Skip redirect if auth is still loading to prevent flickering
    if (isAuthLoading) {
      return;
    }

    // If on a protected route and not authenticated, redirect to login
    // Note: /creations/track/ is now public for sharing, only /playlist/ requires auth
    if (isPlaylistRoute && !isAuthenticated) {
      console.log(
        'User not authenticated for protected player route, redirecting to login...',
      );
      signinRedirect({
        extraQueryParams,
        state: { returnUrl: currentPath },
      }).catch((error) => {
        console.error('Error during sign-in redirect:', error);
      });
    }
  }, [location.pathname, isAuthenticated, isAuthLoading, signinRedirect]);

  // Music player routes: /player/ and /creations/track/ are public, /playlist/ requires authentication
  // Authentication is only required for actions like liking and adding to playlists

  // Check if playback has reached 50% for unauthenticated users
  useEffect(() => {
    if (!Config.ENABLE_50_PERCENT_SIGNUP_MODAL) {
      return;
    }

    // If user is authenticated, don't show modal
    if (isAuthenticated) {
      return;
    }

    // If user seeks back below 50%, allow modal to show again
    if (progress < 50) {
      setHasReachedFiftyPercent(false);
      return;
    }

    // Show modal only when reaching 50% for the first time (or after seeking back below 50%)
    if (duration > 0 && progress >= 50 && !hasReachedFiftyPercent) {
      setIsUnloggedPlaybackModalOpen(true);
      setIsPlaybackBlocked(true);
      setHasReachedFiftyPercent(true);

      // Pause both audio and video when modal shows
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (videoRef.current) {
        videoRef.current.pause();
      }
      setIsPlaying(false);
    }
  }, [progress, duration, isAuthenticated, hasReachedFiftyPercent]);

  // Reset the playback block when track changes
  useEffect(() => {
    setIsPlaybackBlocked(false);
    setHasReachedFiftyPercent(false);
  }, [activePlaylist?.currentIndex]);

  useEffect(() => {
    // Clear any pending seek timeout
    if (miniPlayerSeekTimeoutRef.current) {
      clearTimeout(miniPlayerSeekTimeoutRef.current);
      miniPlayerSeekTimeoutRef.current = null;
    }
    setMiniPlayerDragProgress(0);
    setIsMiniPlayerSeeking(false);
    setIsMiniPlayerDragging(false);
    miniPlayerTargetProgressRef.current = null;
  }, [activePlaylist?.currentIndex]);

  // Prevent playback when at or past 50% and playback is blocked
  // This effect runs continuously to ensure audio and video stay paused at/past 50%
  useEffect(() => {
    // skip modal enable sign up modal is disabled
    if (!Config.ENABLE_50_PERCENT_SIGNUP_MODAL) return;
    if (!isPlaybackBlocked || !audioRef.current || progress < 50) return;

    const audio = audioRef.current;
    const video = videoRef.current;

    const checkAndPauseMedia = () => {
      // Only pause if at or past 50%
      if (progress >= 50) {
        if (!audio.paused) {
          audio.pause();
        }
        if (video && !video.paused) {
          video.pause();
        }
        setIsPlaying(false);
      }
    };

    // Prevent play event from firing on audio when at/past 50%
    const handleAudioPlayAttempt = (e: Event) => {
      if (progress >= 50) {
        e.preventDefault();
        audio.pause();
        if (video) video.pause();
        setIsPlaying(false);
      }
    };

    // Prevent play event from firing on video when at/past 50%
    const handleVideoPlayAttempt = (e: Event) => {
      if (progress >= 50) {
        console.log(
          '🎵 Video play attempt blocked by event listener at/past 50%',
        );
        e.preventDefault();
        if (video) video.pause();
        audio.pause();
        setIsPlaying(false);
      }
    };

    // Check immediately
    checkAndPauseMedia();

    // Add event listeners to prevent play
    audio.addEventListener('play', handleAudioPlayAttempt);
    if (video) {
      video.addEventListener('play', handleVideoPlayAttempt);
    }

    // Also check every 100ms to catch any attempts to resume
    const interval = setInterval(checkAndPauseMedia, 100);

    return () => {
      clearInterval(interval);
      audio.removeEventListener('play', handleAudioPlayAttempt);
      if (video) {
        video.removeEventListener('play', handleVideoPlayAttempt);
      }
    };
  }, [isPlaybackBlocked, progress]);

  // Shuffle and Repeat handlers
  const handleShuffleToggle = () => {
    setShuffleEnabled(!shuffleEnabled);
  };

  const handleRepeatToggle = () => {
    if (repeatMode === 'off') {
      setRepeatMode('all');
    } else if (repeatMode === 'all') {
      setRepeatMode('one');
    } else {
      setRepeatMode('off');
    }
  };

  // Favorites hook - must be called before any conditional returns
  const { toggleFavorite } = useFavorites();
  const {
    isPlayerPage,
    isLoadingAlbum,
    getLastNonPlayerUrl,
    playlistError,
    clearPlaylistError,
  } = useAppRouting(); // Initialize routing logic

  const closeMiniPlayer = () => {
    // Completely close the minimized player
    setIsClosing(true);
    // setIsMinimized(false);
    setTimeout(() => {
      // Pause audio and video if they exist
      if (audioRef.current && isPlaying) {
        audioRef.current.pause();
      }
      if (videoRef.current && isPlaying) {
        videoRef.current.pause();
      }

      React.startTransition(() => {
        setIsPlaying(false);
        setActivePlaylist(null);
        setActivePopup('');
        setIsMinimized(false);
        setIsClosing(false);
        setHasAnimatedIn(false);
        setIsMinimizing(false);
        setIsMaximizing(false);
        // Set active page after clearing playlist and minimized state
      });

      // Navigate back to home page
      setTimeout(() => {
        if (isPlayerPage) {
          navigate('/');
          // window.history.replaceState(null, '', '/');
        }
      }, 50);
    }, 300);
  };
  const minimizePlayer = async () => {
    setIsMinimizing(true);
    videoRef.current?.pause();
    setIsMinimized(true);

    // Use a frame instead of very small setTimeout (more consistent on iOS)
    requestAnimationFrame(async () => {
      const lastNonPlayerUrl = getLastNonPlayerUrl() || '/';
      console.log('📱 Minimizing player - navigating to:', lastNonPlayerUrl);
      const targetPath = new URL(lastNonPlayerUrl, window.location.href)
        .pathname;

      // If already at target, skip
      if (window.location.pathname === targetPath) {
        console.log('Already at target path');
      } else {
        // 1) Try a normal SPA navigation first (push)
        navigate(lastNonPlayerUrl, { replace: false });

        // 2) Wait a short bit for router to update (100ms is usually enough)
        await wait(120);

        // 3) If address bar still didn't change (iOS or timing issues), apply fallback(s)
        if (window.location.pathname !== targetPath) {
          console.warn(
            'SPA navigate did not update address bar — applying fallback',
          );
          try {
            // replace the current history entry (won't add extra entry)
            window.history.replaceState(null, '', lastNonPlayerUrl);
          } catch (e) {
            console.error('replaceState failed', e);
          }

          // 4) If that still didn't change it, force a full navigation (guaranteed)
          await wait(50);
          if (window.location.pathname !== targetPath) {
            window.location.assign(lastNonPlayerUrl); // hard navigation
          }
        }
      }
    });

    // complete animation
    setTimeout(() => setIsMinimizing(false), 300);
  };
  // const minimizePlayer = () => {
  //   // Start minimizing animation
  //   setIsMinimizing(true);

  //   // Just pause video, don't touch audio
  //   if (videoRef.current) {
  //     videoRef.current.pause();
  //   }

  //   // Minimize the player immediately
  //   setIsMinimized(true);

  //   // Navigate to last non-player page
  //   setTimeout(() => {
  //     const lastNonPlayerUrl = getLastNonPlayerUrl();
  //     console.log('📱 Minimizing player - navigating to:', lastNonPlayerUrl);
  //     // Trigger navigation to load the page content
  //     navigate(lastNonPlayerUrl, { replace: true });
  //   }, 100); // Small delay to ensure animation starts

  //   // Complete animation
  //   setTimeout(() => {
  //     setIsMinimizing(false);
  //   }, 300); // Match animation duration
  // };

  const maximizePlayer = () => {
    // Start maximizing animation
    setIsMaximizing(true);

    // Immediately switch to full screen, then animate in
    setIsMinimized(false);

    // After a short delay, complete the animation
    setTimeout(() => {
      // Resume video at current audio time if audio is playing
      if (videoRef.current && isPlaying && audioRef.current) {
        videoRef.current.currentTime = audioRef.current.currentTime;
        videoRef.current.play().catch(() => {
          // Video play failed, but audio continues
        });
      }
      setIsMaximizing(false);
    }, 300); // Match animation duration
  };

  const handleConfirmRemoveFromPlaylist = async (trackId: string) => {
    if (!activePlaylist || activePlaylist.album.type !== 'playlist') {
      return;
    }

    setIsRemoving(true);
    try {
      await removeTrackFromPlaylist(activePlaylist.album.id, trackId);
      showToast.success(t('player.trackRemoved'));

      // Refresh the playlist data
      const updatedPlaylist = await getPlaylistTracks(activePlaylist.album.id);

      if (
        updatedPlaylist &&
        updatedPlaylist.tracks &&
        updatedPlaylist.tracks.length > 0
      ) {
        // Determine which track to play next
        const currentTrackIndex = activePlaylist.currentIndex;
        let newIndex = currentTrackIndex;

        // If the deleted track was the currently playing track
        if (activePlaylist.album.tracks[currentTrackIndex]?.id === trackId) {
          // Try to play the next track
          if (currentTrackIndex < updatedPlaylist.tracks.length) {
            newIndex = currentTrackIndex;
          }
          // If no next track, try to play the previous track
          else if (currentTrackIndex > 0) {
            newIndex = currentTrackIndex - 1;
          }
          // Otherwise, play the first track
          else {
            newIndex = 0;
          }
        } else {
          // If deleted track was before current track, adjust index
          if (currentTrackIndex > 0) {
            newIndex = Math.min(
              currentTrackIndex - 1,
              updatedPlaylist.tracks.length - 1,
            );
          } else {
            newIndex = 0;
          }
        }

        // Stop audio before updating playlist (don't clear src, let useAudioLoader handle it)
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }

        // Update the active playlist with new tracks and new index
        // Force audio reload by creating a new object reference
        const updatedActivePlaylist = {
          ...activePlaylist,
          album: {
            ...activePlaylist.album,
            tracks: updatedPlaylist.tracks,
          },
          currentIndex: newIndex,
        };
        setActivePlaylist(updatedActivePlaylist);

        // Force autoplay of the new track
        setIsPlaying(true);
      } else {
        // No tracks left, close the player completely
        setActivePlaylist(null);
        closeMiniPlayer();
        // Navigate back to the previous page or playlist page
        navigate(-1);
      }
      setConfirmRemoveTrackId(null);

      // Refetch all playlists for this category from API
      const category = activePlaylist.album.category as
        | 'music'
        | 'serenity'
        | 'book';
      try {
        console.log(
          `📡 Fetching ${category} playlists from API after delete...`,
        );
        const response = await getPlaylists(category, 50, 0);

        if (response?.items) {
          console.log(
            `✅ Fetched ${category} playlists (${response.items.length} items)`,
          );
          // Update Redux store with all playlists for this category
          dispatch(
            setPlaylistsByCategory({
              category,
              playlists: response.items,
            }),
          );
          console.log(`📦 Redux store updated with ${category} playlists`);
        }
      } catch (error) {
        console.error('Error refetching playlists after delete:', error);
      }
    } catch (error) {
      console.error('Error removing track from playlist:', error);
      showToast.error(t('player.removeFailed'));
    } finally {
      setIsRemoving(false);
    }
  };

  // Note: Meta tags are now injected server-side for social media sharing
  // No need for client-side injection since the server handles it when the page is requested

  // Handle opening animation when player first appears
  useEffect(() => {
    if (
      activePlaylist &&
      !isMinimized &&
      !hasAnimatedIn &&
      activePopup === 'musicplayer'
    ) {
      // Reset animation state when new playlist starts
      setHasAnimatedIn(false);

      // Delay to ensure DOM is ready and player is positioned off-screen, then trigger slide-up animation
      const openTimer = setTimeout(() => {
        setHasAnimatedIn(true);
      }, 100); // Slightly longer delay to ensure smooth animation

      return () => clearTimeout(openTimer);
    }
  }, [activePlaylist, isMinimized, activePopup]);

  // Minimize player when user navigates to pricing page (only once per navigation)
  useEffect(() => {
    const currentPath = location.pathname;
    const isOnPricingPage =
      currentPath === '/pricing' || currentPath === '/pricing-checkout';

    if (isOnPricingPage) {
      // Only auto-minimize if we haven't already done so for this pricing page visit
      if (
        activePlaylist &&
        activePopup === 'musicplayer' &&
        !isMinimized &&
        !hasAutoMinimizedForPricing
      ) {
        console.log('📱 On pricing page - minimizing player');
        setIsMinimized(true);
        setHasAutoMinimizedForPricing(true);
      }
    } else {
      // Reset the flag when leaving pricing page
      if (hasAutoMinimizedForPricing) {
        setHasAutoMinimizedForPricing(false);
      }
    }
  }, [
    location.pathname,
    activePlaylist,
    activePopup,
    isMinimized,
    hasAutoMinimizedForPricing,
  ]);

  // open full player when new song is selected while minimized
  useEffect(() => {
    if (
      activePlaylist &&
      isMinimized &&
      activePopup === 'musicplayer' &&
      activePlaylist.album.category !== 'generated'
    ) {
      // when a new song is selected and player is minimized, automatically open full player
      setIsMinimized(false);
    }
  }, [activePlaylist?.album.id, activePopup]);

  // Handle minimized player slide-up animation
  useEffect(() => {
    if (isMinimized && !isMaximizing) {
      // Add a small delay to ensure smooth transition from full player
      const timer = setTimeout(() => {
        // The minimized player will slide up from bottom
        // This is handled by the CSS transition in the className
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isMinimized, isMaximizing]);

  // Auto-open Station share panel when song 1 autoplays in Station mode
  useEffect(() => {
    if (
      isStationMode &&
      isMinimized &&
      activePlaylist &&
      activePlaylist.album.category === 'generated' &&
      !hasAutoOpenedSharePanel
    ) {
      // Check if this is the first generated track (song 1)
      const currentTrack =
        activePlaylist.album.tracks[activePlaylist.currentIndex];
      const isSong1 =
        currentTrack?.name === activePlaylist.album.tracks[0]?.name;

      if (isSong1 && isGeneratedTrackSaved()) {
        setIsStationSharePanelOpen(true);
        setHasAutoOpenedSharePanel(true);
      }
    }
  }, [isStationMode, isMinimized, activePlaylist, hasAutoOpenedSharePanel]);

  // Reset auto-open flag when playlist album changes (new generation session)
  useEffect(() => {
    if (activePlaylist?.album.id) {
      setHasAutoOpenedSharePanel(false);
    }
  }, [activePlaylist?.album.id]);

  // Pause music when entering session pages or contributor pages
  useEffect(() => {
    const currentPath = location.pathname;
    const isOnSessionPage =
      currentPath !== '/' && // Not home page
      !currentPath.startsWith('/feeds') &&
      !currentPath.startsWith('/favorites') &&
      !currentPath.startsWith('/musicforyou') &&
      !currentPath.startsWith('/search') &&
      !currentPath.startsWith('/companions') &&
      !currentPath.startsWith('/serenity-wellness') &&
      !currentPath.startsWith('/audiobooks') &&
      !currentPath.startsWith('/contributor/') &&
      !currentPath.startsWith('/player/') &&
      !currentPath.startsWith('/playlists') &&
      !currentPath.startsWith('/my-creations') &&
      !currentPath.startsWith('/creations/') &&
      !currentPath.startsWith('/playlist/') &&
      !currentPath.startsWith('/indian-music') &&
      !currentPath.startsWith('/pricing') &&
      !currentPath.startsWith('/account') &&
      currentPath.split('/').length === 2; // Only one slash means it's a slug route

    // Pause music when entering a session page or contributor page
    if (
      isOnSessionPage &&
      activePlaylist &&
      isPlaying &&
      !hasPausedForSession
    ) {
      console.log('🎵 Pausing music player on session/contributor page entry');
      handlePlayPause();
      setHasPausedForSession(true);
    }

    // Reset the flag when leaving session/contributor page
    if (!isOnSessionPage && hasPausedForSession) {
      setHasPausedForSession(false);
    }

    // OLD CODE: Volume reduction
    // // Only reduce volume once when FIRST entering a session page
    // if (
    //   isOnSessionPage &&
    //   activePlaylist &&
    //   isPlaying &&
    //   !hasReducedVolumeForSession
    // ) {
    //   // Reduce music volume when entering a session page (only if already above 20%)
    //   if (volume > 0.2) {
    //     console.log('🔊 Reducing volume to 20% on session page entry');
    //     _setVolume(0.2);
    //     setHasReducedVolumeForSession(true);
    //   } else {
    //     // Already at or below 20%, just mark as reduced
    //     setHasReducedVolumeForSession(true);
    //   }
    // }
    //
    // // Reset the flag when leaving session page
    // if (!isOnSessionPage && hasReducedVolumeForSession) {
    //   setHasReducedVolumeForSession(false);
    // }
  }, [
    location.pathname,
    activePlaylist,
    isPlaying,
    hasPausedForSession,
    handlePlayPause,
    // OLD CODE dependencie
    // audioRef,
    // videoRef,
    // setIsPlaying,
    // hasReducedVolumeForSession,
    // volume,
    // _setVolume,
  ]);

  // Show loader when loading album data on direct player URL access
  if (!activePlaylist && isLoadingAlbum) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[99999]">
        <div className="flex flex-col items-center gap-4">
          <NapsterSpinner />
          <p className="text-white text-sm">Loading album...</p>
        </div>
      </div>
    );
  }

  // Show error message when playlist/album is not found
  if (!activePlaylist && playlistError) {
    return (
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[99999]">
        <div className="text-center max-w-lg px-6">
          <img
            src="/assets/images/logo_napster_white.svg"
            className="h-[26px] mx-auto mb-10"
            alt="Napster"
          />
          <p className="text-white text-2xl md:text-3xl font-bold leading-snug mb-8">
            {playlistError}
          </p>
          <button
            onClick={() => {
              clearPlaylistError();
              navigate('/');
            }}
            className="px-8 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-lg text-sm font-medium transition-colors duration-200">
            {t('generation.goHomeButton')}
          </button>
        </div>
      </div>
    );
  }

  if (!activePlaylist) return null;

  // Check if we should hide the player based on current route
  const currentPath = location.pathname;
  const isOnContributorPage = currentPath.startsWith('/contributor/');

  // check if we're on a generation page
  const isOnGenerationPage = currentPath.startsWith('/generation/');

  // More reliable session page detection using params
  // If we have a 'slug' param, we're likely on a session page
  const isOnSessionPage =
    Boolean(params.slug) &&
    !isOnContributorPage &&
    !currentPath.startsWith('/player/') &&
    !currentPath.startsWith('/playlist/') &&
    !currentPath.startsWith('/creations/track/') &&
    !isOnGenerationPage;

  // Alternative detection: check against known routes
  const knownRoutes = [
    '/',
    '/feeds',
    '/favorites',
    '/musicforyou',
    '/search',
    '/companions',
    '/serenity-wellness',
    '/audiobooks',
    '/my-creations',
    '/creations',
    '/playlists',
    '/pricing',
    '/indian-music',
    '/indian-music-leaderboard',
  ];
  const isKnownRoute =
    knownRoutes.includes(currentPath) ||
    currentPath.startsWith('/contributor/') ||
    currentPath.startsWith('/player/') ||
    currentPath.startsWith('/account');
  const isOnSessionPageAlt =
    !isKnownRoute && currentPath.split('/').length === 2;

  // Use either detection method
  const finalIsOnSessionPage = isOnSessionPage || isOnSessionPageAlt;

  const currentTrack = activePlaylist.album.tracks[activePlaylist.currentIndex];

  // For playlists, use track contributors; for albums, use album contributors
  const contributors =
    activePlaylist.album.type === 'playlist'
      ? currentTrack?.contributors
      : activePlaylist.album?.contributors;

  const artistImage = contributors?.[0]?.thumbnailUrl;

  // Video URL priority for playlists vs albums:
  // - For playlists: prefer track-level data (currentTrack.album or contributors) over playlist.album
  //   because playlist.album video URLs may be stale from a previous creation track
  // - For albums: use album-level data as primary source
  const isPlaylist = activePlaylist.album.type === 'playlist';
  const videoDeskTop = isPlaylist
    ? currentTrack?.album?.videoDesktopUrl ||
      contributors?.[0]?.videoDesktopUrl ||
      activePlaylist.album.videoDesktopUrl
    : currentTrack?.album?.videoDesktopUrl ||
      activePlaylist.album.videoDesktopUrl ||
      contributors?.[0]?.videoDesktopUrl;
  const videoMobile = isPlaylist
    ? currentTrack?.album?.videoMobileUrl ||
      contributors?.[0]?.videoMobileUrl ||
      activePlaylist.album.videoMobileUrl
    : currentTrack?.album?.videoMobileUrl ||
      activePlaylist.album.videoMobileUrl ||
      contributors?.[0]?.videoMobileUrl;

  const videoUrl = isDesktop
    ? videoDeskTop || videoMobile // Desktop: prefer desktop, fallback to mobile
    : videoMobile || videoDeskTop; // Mobile: prefer mobile, fallback to desktop

  // Poster URL priority follows same pattern as video URLs
  const posterMobile = isPlaylist
    ? currentTrack?.album?.videoMobilePreviewUrl ||
      contributors?.[0]?.videoMobilePreviewUrl ||
      activePlaylist.album.videoMobilePreviewUrl
    : currentTrack?.album?.videoMobilePreviewUrl ||
      activePlaylist.album.videoMobilePreviewUrl ||
      contributors?.[0]?.videoMobilePreviewUrl;
  const posterDesktop = isPlaylist
    ? currentTrack?.album?.videoDesktopPreviewUrl ||
      contributors?.[0]?.videoDesktopPreviewUrl ||
      activePlaylist.album.videoDesktopPreviewUrl
    : currentTrack?.album?.videoDesktopPreviewUrl ||
      activePlaylist.album.videoDesktopPreviewUrl ||
      contributors?.[0]?.videoDesktopPreviewUrl;

  const poster = isDesktop
    ? posterDesktop || posterMobile || contributors?.[0]?.thumbnailUrl // Desktop: prefer desktop, fallback to mobile, then thumbnail
    : posterMobile || posterDesktop || contributors?.[0]?.thumbnailUrl; // Mobile: prefer mobile, fallback to desktop, then thumbnail
  const albumArt =
    currentTrack?.album?.thumbnailUrl || activePlaylist.album.albumArtUrl;

  // Get creator name for creations (my-creations, creation category, or itemType='creation')
  const isCreationCategory =
    activePlaylist.album.category === 'my-creations' ||
    activePlaylist.album.category === 'creation' ||
    (currentTrack as any)?.itemType === 'creation';
  // For creations: use creator.fullName (the user who created the track)
  // Priority: track.creator > album.creator (fallback for my-creations page)
  const creatorName = isCreationCategory
    ? (currentTrack as any)?.creator?.fullName ||
      activePlaylist.album.creator?.fullName
    : undefined;

  return (
    <>
      {/* Hidden audio - Always present for continuous playback */}
      <audio
        ref={audioRef}
        onEnded={handleEnded}
        preload="metadata"
        aria-label="Music"
        playsInline
        webkit-playsinline="true"
        hidden
      />
      {/* Minimized Player (Bottom Bar) */}
      {isMinimized && activePlaylist && (
        <div
          className={`na-musicplayer fixed bottom-0 left-0 right-0 bg-black/30 backdrop-blur-2xl border-t border-white/10 shadow-[0_-4px_40px_rgba(0,0,0,0.4) ${
            finalIsOnSessionPage || isOnGenerationPage || isStationMode
              ? 'sm:left-0 sm:right-0' // Full width on generation pages or Station mode
              : 'sm:left-[215px] sm:right-0'
          }  z-[99999] transition-transform duration-300 ease-out ${
            isClosing
              ? 'translate-y-full'
              : isMaximizing
                ? 'translate-y-full' // Slide down when maximizing
                : 'translate-y-0' // Normal position
          }`}>
          {/* Station Share Panel */}
          {isStationMode &&
            activePlaylist.album.category === 'generated' &&
            isGeneratedTrackSaved() && (
              <StationSharePanel
                key={generateShareUrl()}
                isOpen={isStationSharePanelOpen}
                onClose={() => setIsStationSharePanelOpen(false)}
                shareUrl={generateShareUrl()}
              />
            )}
          {/* Mini Progress Bar */}
          <div
            ref={miniPlayerBarRef}
            className={`w-full h-[4px] bg-white/30 relative rounded-full ${
              duration > 0 && !isNaN(duration)
                ? 'cursor-pointer'
                : 'cursor-not-allowed opacity-70'
            }`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              // Check if seeking is possible
              if (duration <= 0 || isNaN(duration)) {
                console.warn('Mini player seeking disabled: Invalid duration', {
                  duration,
                });
                return;
              }

              const rect = e.currentTarget.getBoundingClientRect();
              const x = Math.max(
                0,
                Math.min(e.clientX - rect.left, rect.width),
              );
              const percentage = (x / rect.width) * 100;

              handleSeekWithBlockCheck(percentage);
            }}
            onMouseDown={handleMiniPlayerDragStart}
            onTouchStart={handleMiniPlayerDragStart}
            title={
              duration > 0 && !isNaN(duration)
                ? 'Click or drag to seek'
                : 'Seeking unavailable - audio not ready'
            }>
            <div
              className={`absolute left-0 top-0 h-full bg-white/60 rounded-full ${
                isMiniPlayerDragging || isMiniPlayerSeeking
                  ? ''
                  : 'transition-all duration-100 ease-linear'
              }`}
              style={{
                width: `${Math.max(
                  isMiniPlayerDragging || isMiniPlayerSeeking
                    ? miniPlayerDragProgress
                    : progress,
                  0,
                )}%`,
                minWidth:
                  (isMiniPlayerDragging || isMiniPlayerSeeking
                    ? miniPlayerDragProgress
                    : progress) > 0
                    ? '2px'
                    : '0px',
              }}
              title={`Progress: ${(isMiniPlayerDragging || isMiniPlayerSeeking
                ? miniPlayerDragProgress
                : progress
              ).toFixed(1)}%`}
            />
            <div
              className={`absolute top-1/2 -translate-y-1/2 w-[12px] h-[12px] rounded-full bg-white shadow ${
                isMiniPlayerDragging || isMiniPlayerSeeking
                  ? ''
                  : 'transition-all duration-100 ease-linear'
              }`}
              style={{
                left: `calc(${Math.max(
                  isMiniPlayerDragging || isMiniPlayerSeeking
                    ? miniPlayerDragProgress
                    : progress,
                  0,
                )}% - 6px)`,
              }}
            />
          </div>

          <div
            className={`flex items-center gap-2 p-2 relative ${
              isStationMode ? '' : 'sm:gap-4 sm:p-3'
            }`}>
            {/* Left Side - Track Info (Left Aligned) */}
            <div
              className={`flex items-center gap-2 flex-1 min-w-0 ${
                isStationMode ? '' : 'sm:gap-3'
              }`}>
              <img
                src={artistImage || albumArt}
                alt={currentTrack.name}
                className={`w-12 h-12 rounded-lg object-cover cursor-pointer flex-shrink-0 ${
                  isStationMode ? '' : 'sm:w-14 sm:h-14'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  maximizePlayer();
                }}
              />
              <div
                className="flex flex-col justify-center min-w-0 cursor-pointer text-left"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  maximizePlayer();
                }}>
                <h4 className="text-white font-medium truncate text-sm leading-tight">
                  {currentTrack.name}
                </h4>
                <div className="text-white/60 text-xs truncate leading-tight mt-0.5">
                  {activePlaylist.album.contributors[0]?.givenName}
                </div>
                <div
                  className={`text-white/40 text-[10px] truncate leading-tight mt-0.5 ${
                    isStationMode ? 'hidden' : 'hidden sm:block'
                  }`}>
                  {activePlaylist?.album.albumName}
                </div>
              </div>
            </div>

            {/* Center - Playback Controls (Prev, Play, Next) - Absolutely Centered */}
            <div
              className={`flex items-center gap-1 flex-shrink-0 ${
                isStationMode
                  ? ''
                  : 'sm:gap-2 sm:absolute sm:left-1/2 sm:transform sm:-translate-x-1/2'
              }`}>
              {/* Previous */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handlePrev();
                }}
                disabled={
                  !activePlaylist ||
                  (activePlaylist.currentIndex === 0 &&
                    repeatMode === 'off' &&
                    !shuffleEnabled)
                }
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-white disabled:opacity-30 disabled:cursor-not-allowed"
                title={t('aria.previousSlide')}
                type="button">
                <PrevIcon width={18} height={18} />
              </button>

              {/* Play/Pause - Centered and Prominent */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handlePlayPauseWithBlockCheck();
                }}
                disabled={isLoading || isBuffering}
                className={`p-2.5 hover:bg-white/20 rounded-full transition-colors text-white bg-white/10 disabled:opacity-70 ${
                  isStationMode ? '' : 'sm:p-3'
                } ${isBuffering ? 'cursor-wait' : ''}`}
                type="button">
                {isLoading || isBuffering ? (
                  <NapsterSpinner size="small" />
                ) : isPlaying ? (
                  <PauseIcon width={20} height={20} />
                ) : (
                  <PlayIcon width={20} height={20} />
                )}
              </button>

              {/* Next */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleNext();
                }}
                disabled={
                  !activePlaylist ||
                  (activePlaylist.currentIndex >=
                    activePlaylist.album.tracks.length - 1 &&
                    repeatMode === 'off' &&
                    !shuffleEnabled)
                }
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-white disabled:opacity-30 disabled:cursor-not-allowed"
                title={t('aria.nextSlide')}
                type="button">
                <PlayNextIcon width={18} height={18} />
              </button>
            </div>

            {/* Right Side - Additional Controls (Hidden on Mobile) */}
            <div
              className={`hidden items-center gap-1 flex-shrink-0 ${
                isStationMode ? '' : 'sm:flex'
              }`}>
              {/* Language Selector */}
              <LanguageSelector size={24} className="" />
              {/* Like - hide for creations (by category or itemType) */}
              {activePlaylist?.album.category !== 'generated' &&
                activePlaylist?.album.category !== 'my-creations' &&
                activePlaylist?.album.category !== 'creation' &&
                (currentTrack as any)?.itemType !== 'creation' && (
                  <>
                    <Tooltip
                      content={currentTrack?.isLiked ? 'Unlike' : 'Like'}
                      anchor="#favorite-button-minimized"
                      placement="top"
                    />
                    <button
                      id="favorite-button-minimized"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleFavorite(
                          'track',
                          currentTrack.id,
                          currentTrack.isLiked,
                          currentTrack.name,
                          contributor?.givenName,
                          activePlaylist.album.category,
                        );
                      }}
                      className="p-2 hover:bg-white/10 rounded-full transition-colors"
                      type="button">
                      {currentTrack?.isLiked ? (
                        <Likedicon width={24} height={27} />
                      ) : (
                        <FavoriteIcon
                          fillOpacity={1}
                          fill="white"
                          width={24}
                          height={27}
                        />
                      )}
                    </button>
                  </>
                )}
              {/* Add to My Playlists Button (show for all except generated songs) */}
              {activePlaylist?.album.category !== 'generated' && (
                <>
                  <Tooltip
                    content={t('player.addToPlaylists')}
                    anchor="#add-to-playlist-button-minimized"
                    placement="top"
                  />
                  <button
                    id="add-to-playlist-button-minimized"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAddToPlaylistClick();
                    }}
                    className="p-2 hover:bg-white/10 rounded-full transition-colors text-white"
                    type="button">
                    <Icon name="playlist-add" size={24} color="currentColor" />
                  </button>
                </>
              )}
              {/* Download Button (only show for my-creations) */}
              {activePlaylist?.album.category === 'my-creations' && (
                <>
                  <Tooltip
                    content={t('player.download')}
                    anchor="#download-button-minimized"
                    placement="top"
                  />
                  <button
                    id="download-button-minimized"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDownloadCreation();
                    }}
                    className="p-2 hover:bg-white/10 rounded-full transition-colors text-white"
                    type="button">
                    <DownloadIcon width={24} height={24} color="white" />
                  </button>
                </>
              )}
              {isGeneratedTrackSaved() && (
                <div className="relative">
                  <>
                    <Tooltip
                      content={t('player.share')}
                      anchor="#share-button-minimized"
                      placement="top"
                    />
                    <button
                      id="share-button-minimized"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsShareTooltipOpen(!isShareTooltipOpen);
                      }}
                      className="p-2 hover:bg-white/10 rounded-full transition-colors text-white"
                      type="button">
                      <ForwardIcon width={24} height={24} />
                    </button>
                  </>
                  {/* Share Tooltip - Station modal is rendered at root level */}
                  {activePlaylist &&
                    !(
                      isStationMode &&
                      activePlaylist.album.category === 'generated'
                    ) && (
                      <ShareTooltip
                        key={generateShareUrl()}
                        isOpen={isShareTooltipOpen}
                        onClose={() => setIsShareTooltipOpen(false)}
                        trackName={
                          activePlaylist.album.tracks[
                            activePlaylist.currentIndex
                          ]?.name || 'Unknown Track'
                        }
                        albumName={activePlaylist.album.albumName}
                        artistName={
                          activePlaylist.album.contributors[0]?.givenName ||
                          'Unknown Artist'
                        }
                        shareUrl={generateShareUrl()}
                        category={activePlaylist.album.category}
                      />
                    )}
                </div>
              )}
              {/* Shuffle */}
              <>
                <Tooltip
                  content={
                    activePlaylist?.album.category === 'book'
                      ? 'Not available for this content type'
                      : shuffleEnabled
                        ? 'Shuffle On'
                        : 'Shuffle Off'
                  }
                  anchor="#shuffle-button-minimized"
                  placement="top"
                />
                <button
                  id="shuffle-button-minimized"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (activePlaylist?.album.category !== 'book') {
                      handleShuffleToggle();
                    }
                  }}
                  disabled={activePlaylist?.album.category === 'book'}
                  className={`p-2 rounded-full transition-colors ${
                    activePlaylist?.album.category === 'book'
                      ? 'text-white cursor-not-allowed opacity-70'
                      : shuffleEnabled
                        ? 'bg-orange-500 text-black'
                        : 'hover:bg-white/10 text-white'
                  }`}
                  type="button">
                  <Icon name="shuffle" size={24} color="currentColor" />
                </button>
              </>
              {/* Repeat */}
              <>
                <Tooltip
                  content={
                    repeatMode === 'off'
                      ? 'Repeat Off'
                      : repeatMode === 'all'
                        ? 'Repeat All'
                        : 'Repeat One'
                  }
                  anchor="#repeat-button-minimized"
                  placement="top"
                />
                <button
                  id="repeat-button-minimized"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRepeatToggle();
                  }}
                  className={`p-2 rounded-full transition-colors relative ${
                    repeatMode !== 'off'
                      ? 'bg-orange-500 text-black'
                      : 'hover:bg-white/10 text-white'
                  }`}
                  type="button">
                  <Icon
                    name={repeatMode === 'one' ? 'repeat-one' : 'repeat'}
                    size={24}
                    color="currentColor"
                  />
                </button>
              </>
              {/* Volume Control */}
              <div className="flex items-center">
                <VolumeControl
                  volume={volume}
                  onVolumeChange={handleVolumeChange}
                  size={24}
                />
              </div>
              {/* Time Display - Hidden below 915px */}
              <div className="hidden text-white text-[16px] whitespace-nowrap px-2 min-w-[107px] min-[915px]:flex">
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>
              {/* Expand */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  maximizePlayer();
                }}
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/60"
                title={t('player.expandQueue')}
                type="button">
                <UpArrowIcon size={24} />
              </button>
              {/* Close */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  closeMiniPlayer();
                }}
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/60"
                title={t('generation.close')}
                type="button">
                <CloseIcon width={24} height={24} />
              </button>
            </div>

            {/* Mobile Right Side - Language Selector and Like */}
            <div
              className={`flex items-center gap-1 flex-shrink-0 ${
                isStationMode ? '' : 'sm:hidden'
              }`}>
              {/* Volume Control - Station only; the desktop Additional Controls group (with its volume) is hidden in Station mode */}
              {isStationMode && (
                <div className="mx-2 bg-white/10 backdrop-blur-sm rounded-full">
                  <VolumeControl
                    volume={volume}
                    onVolumeChange={handleVolumeChange}
                    size={22}
                    stationSliderPlacement="top"
                  />
                </div>
              )}
              {/* Language Selector */}
              <LanguageSelector size={22} className="" />
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  closeMiniPlayer();
                }}
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-white/60"
                title={t('generation.close')}
                type="button">
                <CloseIcon width={24} height={24} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Player */}
      {!isMinimized && activePopup === 'musicplayer' && (
        <div
          id="musicplayer"
          className={`z-[99999] bg-black  flex absolute w-full h-full bottom-0 left-0  transition-all duration-300 ease-out font-[NapsterSans] ${
            isClosing
              ? 'translate-y-full'
              : isMinimizing
                ? 'translate-y-full' // Slide down when minimizing
                : isMaximizing
                  ? 'translate-y-0' // Slide up when maximizing
                  : hasAnimatedIn &&
                      activePopup === 'musicplayer' &&
                      !isMinimized
                    ? 'translate-y-0' // Slide up after animation delay
                    : 'translate-y-full' // Start off-screen
          }  ${
            isOnGenerationPage || isStationMode
              ? 'sm:left-0 sm:right-0 w-full' // Full width on generation pages or Station mode
              : 'sm:w-[calc(100%-215px)] sm:left-[215px]'
          }`}>
          {/* HEADER */}
          <div
            className={`flex px-4 pt-5 pb-16 absolute top-0 left-0 justify-between bg-[linear-gradient(180deg,rgba(0,0,0,0.4)_49.62%,rgba(0,0,0,0)_99.62%)] z-[999] transition-all duration-300 ${
              playlistOpen && !isStationMode
                ? 'sm:w-[calc(100%-384px)] w-full'
                : 'w-full'
            }`}>
            <div className="flex items-center gap-[8px] ">
              <div className="flex text-xs font-semibold leading-[130%] text-[#FCFCFC]">
                {activePlaylist?.album.albumName}
              </div>
            </div>
            <div className="flex gap-2">
              {/* Mobile minimize button */}
              <button
                className="flex cursor-pointer p-2 sm:hidden rounded-full bg-black/40 hover:bg-black/60 transition-colors"
                onClick={minimizePlayer}>
                <CloseIcon />
              </button>
              {!playlistOpen && (
                <button
                  className="cursor-pointer p-2 hidden sm:flex rounded-full bg-black/40 hover:bg-black/60 transition-colors"
                  onClick={minimizePlayer}>
                  <CloseIcon />
                </button>
              )}

              {/* <button><PodcastIcon2/></button> */}
            </div>
          </div>

          {/* BACKGROUND VIDEO OR IMAGE */}
          <div
            className={`flex before:content-[''] before:absolute before:h-full before:bg-[rgba(0,0,0,0.3)] transition-all duration-300 ${
              playlistOpen
                ? playlistOpen && !isStationMode
                  ? 'sm:w-[calc(100%-384px)] sm:before:w-[calc(100%-384px)] w-full before:w-full'
                  : 'w-full before:w-full'
                : 'w-full before:w-full'
            }`}>
            {activePlaylist &&
              (() => {
                // Determine what media to show - only create ONE element to prevent duplicate loading
                const hasVideo = videoUrl && videoUrl.trim() !== '';
                const hasAlbumArt =
                  currentTrack?.album?.thumbnailUrl ||
                  activePlaylist?.album?.albumArtUrl;

                // For my-creations tracks, always show album art instead of video
                if (showAlbumArtForCreations && hasAlbumArt) {
                  return (
                    <img
                      className="h-full w-full object-cover"
                      src={albumArt}
                      alt={activePlaylist?.album?.albumName}
                      loading="lazy"
                    />
                  );
                }

                // Priority: Video (mobile-first on mobile, desktop-first on desktop) > Album art > Default video
                if (hasVideo) {
                  // Use video with available URL
                  return (
                    <video
                      ref={videoRef}
                      className="h-full w-full object-cover"
                      src={videoUrl}
                      poster={poster || undefined}
                      muted
                      autoPlay
                      loop
                      playsInline
                      webkit-playsinline="true"
                      preload="metadata"
                      aria-label="Music"
                      role="img"
                      aria-hidden="true"
                    />
                  );
                } else if (hasAlbumArt) {
                  // Show album art image
                  return (
                    <img
                      className="h-full w-full object-cover"
                      src={albumArt}
                      alt={activePlaylist?.album?.albumName}
                      loading="lazy"
                    />
                  );
                } else {
                  // Fallback to default video - only if no other media available
                  return (
                    <video
                      ref={videoRef}
                      className="h-full w-full object-cover"
                      poster={poster || undefined}
                      src={
                        isDesktop
                          ? placeHolderData.contributorDesktopVideo
                          : placeHolderData.contributorMobileVideo
                      }
                      muted
                      autoPlay
                      loop
                      playsInline
                      webkit-playsinline="true"
                      preload="metadata"
                      aria-label="Music"
                      role="img"
                      aria-hidden="true"
                    />
                  );
                }
              })()}
          </div>

          {/* Loading Indicator */}
          {isLoading && (
            <div
              className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 transition-all duration-300 ${
                playlistOpen && !isStationMode
                  ? 'sm:right-[384px] right-0'
                  : 'right-0'
              }`}>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 flex flex-col items-center gap-4">
                <NapsterSpinner size="large" />
                <div className="text-white text-sm">Loading...</div>
              </div>
            </div>
          )}

          {/* Mobile Audio Unlock Overlay - Only for direct URL access */}
          {!userHasInteracted && !isLoading && activePlaylist && (
            <div
              className={`fixed inset-0 bg-black/70 flex items-center justify-center z-50 transition-all duration-300 ${
                playlistOpen && !isStationMode
                  ? 'sm:right-[384px] right-0'
                  : 'right-0'
              }`}>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 flex flex-col items-center gap-4 max-w-sm mx-4">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                  <PlayIcon width={24} height={24} />
                </div>
                <div className="text-white text-center">
                  <h3 className="text-lg font-semibold mb-2">Ready to Play</h3>
                  <p className="text-sm text-white/80 mb-4">
                    Tap to start your music
                  </p>
                </div>
                <button
                  onClick={handlePlayPause}
                  className="bg-white text-black px-6 py-3 rounded-full font-semibold hover:bg-white/90 transition-colors">
                  Start Playing
                </button>
              </div>
            </div>
          )}

          <MusicInfo
            contributorId={contributor?.id || ''}
            slug={contributor?.slug || ''}
            progress={progress}
            onSeek={handleSeekWithBlockCheck}
            currentTime={currentTime}
            duration={duration}
            formatTime={formatTime}
            playlistOpen={playlistOpen}
            volume={volume}
            onVolumeChange={handleVolumeChange}
            isPlaying={isPlaying}
            onPlayPause={handlePlayPauseWithBlockCheck}
            onNext={handleNext}
            onPrev={handlePrev}
            albumArtUrl={albumArt}
            creatorName={creatorName}
            videoPosterUrl={showAlbumArtForCreations ? poster : undefined}
          />

          {/* Pass controls to child */}
          <PlayerControls
            contributorId={contributor?.id || ''}
            slug={contributor?.slug || ''}
            isPlaying={isPlaying}
            onPlayPause={handlePlayPauseWithBlockCheck}
            onNext={handleNext}
            onPrev={handlePrev}
            onTrackSelect={handleTrackSelect}
            playlistOpen={playlistOpen}
            volume={volume}
            onVolumeChange={handleVolumeChange}
            isLoading={isLoading}
            isBuffering={isBuffering}
            onMinimize={minimizePlayer}
            audioRef={audioRef}
          />
        </div>
      )}

      {/* Add to My Playlists Modal (show for all except generated songs) */}
      {activePlaylist && activePlaylist.album.category !== 'generated' && (
        <AddToPlaylistModal
          isOpen={isAddToPlaylistModalOpen}
          onClose={() => setIsAddToPlaylistModalOpen(false)}
          trackId={
            activePlaylist.album.tracks[activePlaylist.currentIndex]?.id || ''
          }
          trackName={
            activePlaylist.album.tracks[activePlaylist.currentIndex]?.name || ''
          }
          creatorName={
            activePlaylist.album.type === 'playlist'
              ? activePlaylist.album.tracks[activePlaylist.currentIndex]
                  ?.contributors?.[0]?.givenName
              : activePlaylist.album.contributors?.[0]?.givenName
          }
          category={
            // For creations (my-creations category or itemType='creation'), use 'music'
            // so creations are added to music playlists, not a separate creation category
            activePlaylist.album.category === 'my-creations' ||
            activePlaylist.album.category === 'creation' ||
            (currentTrack as any)?.itemType === 'creation'
              ? 'music'
              : (activePlaylist.album.category as
                  | 'music'
                  | 'serenity'
                  | 'book'
                  | 'creation')
          }
          onTrackAdded={handleTrackAddedToPlaylist}
          // Pass creation-specific props when playing my-creations or creation playlists
          itemType={
            activePlaylist.album.category === 'my-creations' ||
            activePlaylist.album.category === 'creation' ||
            (currentTrack as any)?.itemType === 'creation'
              ? 'creation'
              : undefined
          }
          itemId={
            activePlaylist.album.category === 'my-creations' ||
            activePlaylist.album.category === 'creation' ||
            (currentTrack as any)?.itemType === 'creation'
              ? currentTrack?.id
              : undefined
          }
          creationOrgId={
            activePlaylist.album.category === 'my-creations'
              ? (activePlaylist.album as any)?.orgId
              : activePlaylist.album.category === 'creation' ||
                  (currentTrack as any)?.itemType === 'creation'
                ? (currentTrack as any)?.creationOrgId ||
                  (currentTrack as any)?.orgId
                : undefined
          }
        />
      )}

      {/* Confirm Remove from Playlist Modal */}
      <ConfirmRemoveModal
        isOpen={confirmRemoveTrackId !== null}
        trackName={confirmRemoveTrackName}
        onConfirm={() => {
          if (confirmRemoveTrackId) {
            handleConfirmRemoveFromPlaylist(confirmRemoveTrackId);
          }
        }}
        onCancel={() => {
          setConfirmRemoveTrackId(null);
          setConfirmRemoveTrackName('');
        }}
        isLoading={isRemoving}
      />

      {/* Unlogged Playback Modal - Shows at 50% duration for unauthenticated users */}
      <UnloggedSignupModal
        isOpen={isUnloggedPlaybackModalOpen}
        onClose={() => {
          // Close modal but keep playback blocked at 50%
          setIsUnloggedPlaybackModalOpen(false);
          // isPlaybackBlocked remains true to prevent further playback
        }}
      />
    </>
  );
};
